import { useParams, useHistory } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetUserById } from '../../users/hooks/admin-get-user-by-id.hook';
import { useGetLastUserOrders } from '../hooks/get-last-user-orders.hook';
import { useAdminCreateOrder } from '../hooks/admin-create-order.hook';
import { OrderFormView } from './components/OrderFormView.component';
import { OrderFormState } from '../utils/order-to-form-state.util';
import { ADMIN_USERS_APP_PATHS } from '../../users/constants/users-paths.constants';
import { NotFoundState } from '../../shared/components/States/NotFoundState.component';
import { ADMIN_ORDER_APP_PATHS } from '../constants/orders-app-paths.constant';
import { DeliveryAddress } from '../types/delivery-selection.type';
import { ORDER_STATUSES } from '../types/order-status.type';
import { ORDER_ADV_STATUSES } from '../types/order-adv-status.type';
import { AdminCreateOrderRequestDTO, OrderOwnerDTO } from '../types/dtos/admin-create-order.dto';

function toAddressPayload(addr: DeliveryAddress) {
  return {
    address: addr.address,
    zipCode: addr.zipCode,
    city: addr.city,
    ...(addr.title && { title: addr.title }),
    ...(addr.complement && { complement: addr.complement }),
    ...(addr.comment && { comment: addr.comment }),
  };
}

export default function AdminCreateOrderView() {
  const { userId } = useParams<{ userId: string }>();
  const history = useHistory();

  const { isLoading: isLoadingUser, user } = useAdminGetUserById({ userId });
  const { lastOrder } = useGetLastUserOrders(userId);
  const { isCreating, createOrder } = useAdminCreateOrder();

  const isLoading = isLoadingUser;
  if (isLoading) return <Skeleton variant="text" width="100%" height={50} />;

  if (!user) {
    return (
      <NotFoundState
        title="Client introuvable"
        backLabel="Retour aux clients"
        backTo={ADMIN_USERS_APP_PATHS.USERS_LIST}
      />
    );
  }

  if (user.isActive === false) {
    return (
      <NotFoundState
        title="Ce client est désactivé"
        backLabel="Retour à la fiche"
        backTo={ADMIN_USERS_APP_PATHS.USER_DETAILS(userId)}
      />
    );
  }

  const addresses = user.addresses ?? [];
  const owner: OrderOwnerDTO = {
    id: userId,
    firstName: user.firstname,
    lastName: user.lastname,
    phoneNumber: user.phoneNumber,
  };

  const initialValues: OrderFormState = {
    items: [],
    deliveryType: 'home',
    deliveryAddress: null,
    billingAddress: null,
    deliveryDay: null,
    deliveryPointId: null,
    organizationId: null,
    status: ORDER_STATUSES.PAID,
    advStatus: ORDER_ADV_STATUSES.INPUT,
    deliveryCost: 0,
  };

  const handleSubmit = async (state: OrderFormState) => {
    if (!state.deliveryAddress || !state.deliveryDay) return;

    const sharedItems = {
      products: state.items
        .filter((item) => item.type !== 'basket' && item.type !== 'virtual')
        .map((item) => ({ id: item.productId ?? '', type: 'product' as const, quantity: item.quantity })),
      baskets: state.items
        .filter((item) => item.type === 'basket')
        .map((item) => ({ id: item.productId ?? '', type: 'basket' as const, quantity: item.quantity })),
      virtuals: state.items
        .filter((item) => item.type === 'virtual')
        .map((item) => ({
          type: 'virtual' as const,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          tvaRate: item.tvaRate,
          size: item.size,
          noLabel: item.noLabel,
          valueZero: item.valueZero,
        })),
      status: state.status,
      advStatus: state.advStatus,
      deliveryCost: state.deliveryCost,
    };

    let payload: AdminCreateOrderRequestDTO;

    switch (state.deliveryType) {
      case 'home': {
        const billingAddr = state.billingAddress ?? state.deliveryAddress;
        payload = {
          owner,
          deliveryType: 'home',
          deliveryDate: state.deliveryDay.date.format('YYYY-MM-DD'),
          deliverySchedule: state.deliveryDay.schedule,
          deliveryInfos: {
            deliveryId: null,
            deliveryType: 'home',
            deliveryAddress: toAddressPayload(state.deliveryAddress),
            deliveryBillingAddress: toAddressPayload(billingAddr),
            deliveryDate: state.deliveryDay.date.format('YYYY-MM-DD'),
            deliverySchedule: state.deliveryDay.schedule,
          },
          ...sharedItems,
        };
        break;
      }

      case 'relayPoint': {
        payload = {
          owner,
          deliveryType: 'relay',
          relayId: state.deliveryAddress._id,
          deliveryDate: state.deliveryDay.date.format('YYYY-MM-DD'),
          deliverySchedule: state.deliveryDay.schedule,
          ...(state.billingAddress && { billingAddress: toAddressPayload(state.billingAddress) }),
          ...sharedItems,
        };
        break;
      }

      case 'bioculture': {
        payload = {
          owner,
          deliveryType: 'drive',
          deliveryDate: state.deliveryDay.date.format('YYYY-MM-DD'),
          deliverySchedule: state.deliveryDay.schedule,
          ...sharedItems,
        };
        break;
      }

      case 'organization': {
        if (!state.deliveryPointId || !state.organizationId) return;
        payload = {
          owner,
          deliveryType: 'organization',
          organizationId: state.organizationId,
          deliveryPointId: state.deliveryPointId,
          deliveryDate: state.deliveryDay.date.format('YYYY-MM-DD'),
          deliverySchedule: state.deliveryDay.schedule,
          ...(state.billingAddress && { billingAddress: toAddressPayload(state.billingAddress) }),
          ...sharedItems,
        };
        break;
      }
    }

    const created = await createOrder(payload);
    if (created) history.push(ADMIN_ORDER_APP_PATHS.ORDER_DETAILS(created.id));
  };

  return (
    <OrderFormView
      mode="create"
      title={`Création de commande pour ${user.firstname} ${user.lastname}`}
      ctaLabel="Créer la commande"
      addresses={addresses}
      lastOrder={lastOrder}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
